import { useCallback, useMemo, useState } from "react"
import { Interval } from "luxon"
import { makeStyles } from "tss-react/mui"
import { Box, FormControlLabel, Grid, Stack, Switch } from "@mui/material"
import { UseQueryResult } from "@tanstack/react-query"
import { MRT_ColumnDef, MRT_FullScreenToggleButton, MRT_GlobalFilterTextField } from "material-react-table"
import { StandardToggleGlobalFilterButton } from "../../../../../../shared/components/table/StandardToggleGlobalFilterButton"
import { useExportAcquiringReportsBalanceDetailsToCsv } from "../../hooks/useExportAcquiringReportsBalanceDetailsToCsv"
import { DateType } from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    RevenueType,
    BalancePaymentDetails,
    BalanceSelectOrderType,
    TransferItem,
    ParsedPaymentDetails,
    PaymentDetailsSplit as SplitPayments,
} from "../../supplierAcquiringReportsTypes"
import { CompaniesOrderItem } from "../../../../../company/reports/accounting/pos/posTypes"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useApiQueryTable, useCombinedApiQuery } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { DateTimeFactory, useDateTime } from "../../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    TableNumberOfItemsBox,
    TableSummaryBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { SelectOrderTypeFilter } from "./SelectOrderTypeFilter"
import {
    fetchSupplierAcquiringBankTransfers,
    fetchSupplierBalanceDetails,
    SupplierAcquiringBankTransfersResponse,
    SupplierAcquiringReportsQuery,
    SupplierBalanceDetailsResponse,
} from "../../supplierAcquiringReportsApi"
import { useSupplier } from "../../../../supplierHooks"
import {
    fetchSupplierPosOrders,
    SupplierOrdersResponse,
    SupplierPOSQuery,
} from "../../../../reporting/accounting/pos/posApi"
import { DataStateType } from "../../pages/Balance"
import { UnreachableCaseError } from "../../../../../../lib/lang"
import { StandardChip, StandardChipVariantType } from "../../../../../../shared/components/badges/StandardChip"
import { DKK } from "@dinero.js/currencies"
import {
    detectCurrencyFromDinero,
    toDinero,
    toDineroOrUndefined,
    zeroDinero,
} from "../../../../../currency/currencyLib"
import { add, multiply, subtract } from "dinero.js"
import { useMoney } from "../../../../../currency/useMoney"
import { Row } from "@tanstack/table-core"
import { Logger } from "../../../../../../lib/logging"
import { iterateStringEnum } from "../../../../../../lib/enums"
import { classifyPaymentRevenue } from "../../reconciliationLib"
import { useSearchParams } from "react-router-dom"
import { PAGE_QUERY, PAGE_SIZE_QUERY } from "../../../../../../shared/hooks/usePagination"

type BalanceDetailsDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    dateState: DataStateType
}>

const useStyles = makeStyles()(() => ({
    container: {
        margin: "0 auto",
    },
    wrap: {
        overflow: "auto",
    },
    tablePaper: {
        boxShadow: "none",
    },
}))

const selectBankTransfers = (data: SupplierAcquiringBankTransfersResponse) => data.transfers || []
const selectSupplierPosOrders = (data: SupplierOrdersResponse) => data.orders || []

function findBankTransferPeriod(transfers: TransferItem[], period: Interval, dateTimeFactory: DateTimeFactory) {
    const firstInsideIndex = transfers.findIndex((transfer) => {
        const bookingDate = dateTimeFactory.fromApi(transfer.bookingDate)
        return period.contains(bookingDate)
    })

    const lastInsideIndex = transfers.findLastIndex((transfer) => {
        const bookingDate = dateTimeFactory.fromApi(transfer.bookingDate)
        return period.contains(bookingDate)
    })

    const lastBeforeIndex = firstInsideIndex - 1

    if (firstInsideIndex < 0) {
        // No transfers in period, return an empty interval
        return Interval.fromDateTimes(period.end, period.end)
    }
    const transferStartTime =
        lastBeforeIndex >= 0 ? dateTimeFactory.fromApi(transfers[lastBeforeIndex].bookingDate) : period.start
    const transferEndTime =
        lastInsideIndex >= 0 ? dateTimeFactory.fromApi(transfers[lastInsideIndex].bookingDate) : period.end

    return Interval.fromDateTimes(transferStartTime, transferEndTime)
}

function splitPayments(
    allPayments: ParsedPaymentDetails[],
    reportPeriod: Interval,
    transferPeriod: Interval,
): SplitPayments {
    const bankTransfers: ParsedPaymentDetails[] = []
    const deferred: ParsedPaymentDetails[] = []
    const normal: ParsedPaymentDetails[] = []

    for (const payment of allPayments) {
        if (payment.valueDate && transferPeriod.contains(payment.valueDate)) {
            bankTransfers.push(payment)
        }

        if (payment.valueDate && reportPeriod.contains(payment.valueDate)) {
            if (reportPeriod.contains(payment.bookingDate)) {
                normal.push(payment)
            } else if (reportPeriod.isAfter(payment.bookingDate)) {
                deferred.push(payment)
            }
        }
    }

    return {
        bankTransfers,
        deferred,
        normal,
    }
}

function findAccruedOrders(
    posData: CompaniesOrderItem[],
    payments: SplitPayments,
    dateTimeFactory: DateTimeFactory,
): ParsedPaymentDetails[] {
    const existingPaymentIds = new Set(payments.normal.map((payment) => payment.orderId))

    return posData
        .filter((order) => !existingPaymentIds.has(order.id))
        .map((order) => ({
            orderId: order.id,
            orderCreated: order.created,
            pspPaymentReference: "-",
            bookingDate: dateTimeFactory.fromApi(order.created),
            valueDate: undefined,
            grossAmount: toDinero(order.amount),
            netAmount: undefined,
            feeAmount: undefined,
            effectiveAmount: toDinero(order.amount),
            type: undefined,
        }))
}

function findNetDetails(payments: SplitPayments, accruedOrders: ParsedPaymentDetails[]) {
    // Deferred payments should contribute only their fee amount
    const deferredFees: ParsedPaymentDetails[] = payments.deferred.map((payment) => ({
        ...payment,
        grossAmount: undefined,
        netAmount: undefined,
        // API returns wrong net amount, so we calculate client-side
        effectiveAmount: payment.feeAmount ? multiply(payment.feeAmount, -1) : undefined,
    }))

    // Normal payments should contribute their net amount
    const normalPayments: ParsedPaymentDetails[] = payments.normal.map((payment) => ({
        ...payment,
        // API returns wrong net amount, so we calculate client-side
        netAmount:
            payment.grossAmount && payment.feeAmount
                ? subtract(payment.grossAmount, payment.feeAmount)
                : payment.netAmount,
        effectiveAmount:
            payment.grossAmount && payment.feeAmount
                ? subtract(payment.grossAmount, payment.feeAmount)
                : payment.netAmount,
    }))

    // Net = normal payments + accrued orders - deferred fees
    return deferredFees.concat(normalPayments).concat(accruedOrders)
}

function findTransferDetails(payments: SplitPayments) {
    return payments.bankTransfers.map((payment) => ({
        ...payment,
        grossAmount: payment.grossAmount,
        feeAmount: payment.feeAmount,
        // API returns wrong net amount, so we calculate client-side
        netAmount:
            payment.grossAmount && payment.feeAmount
                ? subtract(payment.grossAmount, payment.feeAmount)
                : payment.netAmount,
        effectiveAmount:
            payment.grossAmount && payment.feeAmount
                ? subtract(payment.grossAmount, payment.feeAmount)
                : payment.netAmount,
        type: RevenueType.BANK_TRANSFER, // override revenue type
    }))
}

function findBalanceDetails(netDetails: ParsedPaymentDetails[], bankDetails: ParsedPaymentDetails[]) {
    const bankPlus: ParsedPaymentDetails[] = bankDetails.map((payment) => ({
        ...payment,
        // Make column sums more meaningful by not counting bank transfer payments
        // in the gross and fee columns in the balance view
        grossAmount: undefined,
        feeAmount: undefined,
    }))
    const netMinus: ParsedPaymentDetails[] = netDetails.map((payment) => ({
        ...payment,
        effectiveAmount: payment.effectiveAmount ? multiply(payment.effectiveAmount, -1) : undefined,
    }))

    return bankPlus.concat(netMinus)
}

export function BalanceDetailsDialog({ open, onClose, dateState }: BalanceDetailsDialogProps) {
    const { classes, cx } = useStyles()
    const { supplierId } = useSupplier()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [searchParams, setSearchParams] = useSearchParams()

    const [orderPeriod, setOrderPeriod] = useState<BalanceSelectOrderType>(BalanceSelectOrderType.NET_SALES)
    const [compact, setCompact] = useState(true)

    const selectAndParsePaymentDetails = useCallback(
        (response: SupplierBalanceDetailsResponse) => {
            const parsedPayments: ParsedPaymentDetails[] = response.payments.map((payment: BalancePaymentDetails) => ({
                ...payment,
                bookingDate: dateTimeFactory.fromApi(payment.bookingDate),
                valueDate: dateTimeFactory.fromApiOrUndefined(payment.valueDate),
                grossAmount: toDineroOrUndefined(payment.grossAmount),
                feeAmount: toDineroOrUndefined(payment.feeAmount),
                netAmount: toDineroOrUndefined(payment.netAmount),
                effectiveAmount: undefined,
                type: undefined,
            }))

            return parsedPayments
        },
        [dateTimeFactory],
    )

    // In order to find the payments included in the bank transfers for the period
    // we need to find the last bank transfer before the period start
    const bankTransferQuery = useApiQuery(fetchSupplierAcquiringBankTransfers, {
        queryName: SupplierAcquiringReportsQuery.BANK_TRANSFERS_LIST,
        dependencies: {
            supplierId,
            fromDate: dateState.startDate.minus({ months: 1 }).toISODate(),
            toDate: dateState.endDate.toISODate(),
        },
        select: selectBankTransfers,
    })

    // Since we are dealing with timestamps and not dates, we need to make sure we have the correct boundaries
    const reportPeriod = useMemo(() => {
        const periodStartTimeInclusive = dateState.startDate.startOf("day")
        const periodEndTimeExclusive = dateState.endDate.plus({ days: 1 }).startOf("day")
        const period = Interval.fromDateTimes(periodStartTimeInclusive, periodEndTimeExclusive)

        new Logger("balance-details").info("Report period: ", period.toISO())
        return period
    }, [dateState.startDate, dateState.endDate])

    // The period the bank transfers cover are usually different from the reporting period
    const transferPeriod = useMemo(() => {
        const period = findBankTransferPeriod(bankTransferQuery.data ?? [], reportPeriod, dateTimeFactory)
        new Logger("balance-details").info("Transfer period: ", period)
        return period
    }, [bankTransferQuery.data, reportPeriod, dateTimeFactory])

    // We need to fetch balance details for a wider period than reporting period
    // NOTE: When using transfer period, we need to include the time, when using reporting period, we don't want the time
    const paymentQuery = useApiQuery(fetchSupplierBalanceDetails, {
        queryName: SupplierAcquiringReportsQuery.BALANCE_DETAILS_LIST,
        dependencies: {
            supplierId,
            fromDateTime:
                transferPeriod.start < reportPeriod.start
                    ? transferPeriod.start.toISO()
                    : reportPeriod.start.toISODate(),
            toDateTime:
                transferPeriod.end > reportPeriod.end ? transferPeriod.end.toISO() : reportPeriod.end.toISODate(),
        },
        select: selectAndParsePaymentDetails,
    })

    const orderQuery = useApiQuery(fetchSupplierPosOrders, {
        queryName: SupplierPOSQuery.POS_ORDERS_FOR_SUPPLIER,
        dependencies: {
            supplierId,
            dateType: DateType.DELIVERY_DATE,
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
            channel: "ADYEN_POS",
        },
        select: selectSupplierPosOrders,
    })

    const payments = useMemo(
        () => splitPayments(paymentQuery.data || [], reportPeriod, transferPeriod),
        [paymentQuery.data, reportPeriod, transferPeriod],
    )
    const accruedOrders = useMemo(
        () => findAccruedOrders(orderQuery.data || [], payments, dateTimeFactory),
        [orderQuery.data, payments, dateTimeFactory],
    )
    const netDetails = useMemo(() => findNetDetails(payments, accruedOrders), [payments, accruedOrders])
    const transferDetails = useMemo(() => findTransferDetails(payments), [payments])
    const balanceDetails = useMemo(() => findBalanceDetails(netDetails, transferDetails), [netDetails, transferDetails])

    const tableData = useMemo(() => {
        new Logger("balance-details").info("returning table data")
        switch (orderPeriod) {
            case BalanceSelectOrderType.NET_SALES:
                return netDetails
            case BalanceSelectOrderType.BANK_TRANSFERS:
                return transferDetails
            case BalanceSelectOrderType.BALANCE:
                return balanceDetails
            default:
                throw new UnreachableCaseError(orderPeriod)
        }
    }, [orderPeriod, netDetails, transferDetails, balanceDetails])

    const combinedQueryResult = useCombinedApiQuery(tableData, [paymentQuery, orderQuery, bankTransferQuery])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(combinedQueryResult)

    const columns = useMemo<MRT_ColumnDef<ParsedPaymentDetails>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-overview-balance-details-dialog-table-order-id-column"),
                accessorKey: "orderId",
                muiTableHeadCellProps: {
                    align: "center",
                },
                muiTableBodyCellProps: {
                    align: "center",
                    style: {
                        verticalAlign: "baseline",
                    },
                },
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-overview-balance-details-dialog-table-rsp-reference-column"),
                accessorKey: "pspPaymentReference",
            }),
            columnFactory.parsedDateTimeColumn(
                {
                    id: "bookingDate",
                    header: l10n.getString("supplier-overview-balance-details-dialog-table-booked-column"),
                    accessorKey: "bookingDate",
                },
                (row) => row.bookingDate,
            ),
            columnFactory.parsedDateTimeColumn(
                {
                    id: "valueDate",
                    header: l10n.getString("supplier-overview-balance-details-dialog-table-available-column"),
                    accessorKey: "valueDate",
                },
                (row) => row.valueDate,
            ),

            columnFactory.parsedMoneyColumn(
                {
                    id: "grossAmount",
                    header: l10n.getString("supplier-overview-balance-details-dialog-table-gross-column"),
                },
                (row) => row.grossAmount,
                true,
            ),

            columnFactory.parsedMoneyColumn(
                {
                    id: "feeAmount",
                    header: l10n.getString("supplier-overview-balance-details-dialog-table-fee-column"),
                },
                (row) => row.feeAmount,
                true,
            ),

            columnFactory.parsedMoneyColumn(
                {
                    id: "netAmount",
                    header: l10n.getString("supplier-overview-balance-details-dialog-table-net-column"),
                },
                (row) => row.netAmount,
                true,
            ),

            columnFactory.parsedMoneyColumn(
                {
                    id: "effectiveAmount",
                    header: l10n.getString("supplier-overview-balance-details-dialog-table-effective-column"),
                },
                (row) => row.effectiveAmount,
                true,
            ),

            columnFactory.statusColumn({
                id: "type",
                header: l10n.getString("supplier-overview-balance-details-dialog-table-type-column"),
                accessorFn: (row) => classifyPaymentRevenue(row, reportPeriod),
                filterSelectOptions: [...iterateStringEnum(RevenueType)]
                    .filter((type) => type !== RevenueType.DEFERRED_REVENUE && type !== RevenueType.PREVIOUS_REVENUE)
                    .map((type) => ({
                        value: type,
                        text: l10n.getStringForEnum(
                            "supplier-overview-balance-details-dialog-table-payment-type",
                            type,
                            type,
                        ),
                    })),
                Cell: ({ cell }) => {
                    const paymentType = cell.getValue<RevenueType>()
                    const variant: StandardChipVariantType =
                        paymentType === RevenueType.ACCRUED_REVENUE
                            ? "red"
                            : paymentType === RevenueType.BANK_TRANSFER
                              ? "info"
                              : "success"
                    const label = l10n.getStringForEnum(
                        "supplier-overview-balance-details-dialog-table-payment-type",
                        paymentType,
                        paymentType,
                    )

                    if (!label) {
                        return <></>
                    }

                    return (
                        <StandardChip variant={variant} placement="table">
                            {label}
                        </StandardChip>
                    )
                },
            }),
        ],
        [columnFactory, l10n, reportPeriod],
    )

    const exportDetails = useExportAcquiringReportsBalanceDetailsToCsv(tableData, reportPeriod, orderPeriod)

    const removePageSize = () => {
        searchParams.set(PAGE_QUERY, "0")
        searchParams.set(PAGE_SIZE_QUERY, "10")
        setSearchParams(searchParams)
    }

    return (
        <LocalizedStrict
            id="supplier-overview-balance-details-dialog"
            attrs={{ dialogTitle: true, subtitle: true }}
            vars={{
                periodStart: dateTimeFactory.formatDate(dateState.startDate),
                periodEnd: dateTimeFactory.formatDate(dateState.endDate),
            }}
        >
            <StandardDialog
                open={open}
                onClose={onClose}
                dialogTitle="Balance details"
                subtitle={`Period: ${dateState.startDate.toISODate()} - ${dateState.endDate.toISODate()}`}
                maxWidthProp="xl"
                isFullScreen
            >
                <Box className={classes.container}>
                    <Grid container direction="column" className={classes.wrap}>
                        <ApiQueryTable
                            columns={columns}
                            initialState={{
                                density: "spacious",
                                sorting: [
                                    {
                                        id: "bookingDate",
                                        desc: false,
                                    },
                                ],
                            }}
                            state={{
                                columnVisibility: {
                                    grossAmount: !compact,
                                    feeAmount: !compact,
                                    netAmount: !compact,
                                },
                            }}
                            renderTopToolbar={({ table }) => (
                                <Box>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <StandardTableFilter variant="left">
                                            <SelectOrderTypeFilter
                                                orderPeriod={orderPeriod}
                                                setOrderPeriod={(period) => {
                                                    setOrderPeriod(period)
                                                    removePageSize()
                                                }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={!compact}
                                                        onClick={() => setCompact((prev) => !prev)}
                                                    />
                                                }
                                                label={l10n.getString(
                                                    "supplier-overview-balance-details-dialog-compact-view-label",
                                                )}
                                            />
                                        </StandardTableFilter>
                                        <Grid container flexWrap="nowrap" justifyContent="end" gap={2} padding="1rem">
                                            <Stack direction="row" spacing={1}>
                                                <MRT_GlobalFilterTextField table={table} />
                                                <StandardExportButton onClick={() => exportDetails()} />
                                                <StandardToggleGlobalFilterButton table={table} />
                                                <MRT_FullScreenToggleButton table={table} />
                                            </Stack>
                                        </Grid>
                                    </Box>
                                </Box>
                            )}
                            renderBottomToolbarCustomActions={({ table }) => (
                                <TableSummaryWrap>
                                    <LocalizedStrict
                                        id="supplier-overview-balance-details-dialog-number-of-orders"
                                        attrs={{ title: true }}
                                    >
                                        <TableNumberOfItemsBox
                                            data={table.getFilteredRowModel().rows}
                                            isLoading={table.getState().isLoading}
                                            title="Number of transfers"
                                        />
                                    </LocalizedStrict>

                                    {!compact && orderPeriod !== BalanceSelectOrderType.BANK_TRANSFERS && (
                                        <TableTotalAmountBox
                                            data={table.getFilteredRowModel().rows}
                                            isLoading={table.getState().isLoading}
                                            accessor={(row) => row.original.grossAmount}
                                            localizedKey="supplier-overview-balance-details-dialog-table-gross-total"
                                        />
                                    )}

                                    {!compact && orderPeriod !== BalanceSelectOrderType.BANK_TRANSFERS && (
                                        <TableTotalAmountBox
                                            data={table.getFilteredRowModel().rows}
                                            isLoading={table.getState().isLoading}
                                            accessor={(row) => row.original.feeAmount}
                                            localizedKey="supplier-overview-balance-details-dialog-table-fee-total"
                                        />
                                    )}

                                    <TableTotalAmountBox
                                        data={table.getFilteredRowModel().rows}
                                        isLoading={table.getState().isLoading}
                                        accessor={(row) => row.original.effectiveAmount}
                                        localizedKey="supplier-overview-balance-details-dialog-table-effective-total"
                                    />
                                </TableSummaryWrap>
                            )}
                            muiTablePaperProps={{ className: classes.tablePaper }}
                        />
                    </Grid>
                </Box>
            </StandardDialog>
        </LocalizedStrict>
    )
}
